import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FiMenu, FiX, FiChevronDown } from "react-icons/fi";

const EVENT_TYPES = [
  { label: "Birthdays", to: "/packages", live: true },
  { label: "Baby showers", to: "/events", live: false },
  { label: "Small gatherings", to: "/events", live: false },
  { label: "Housewarmings", to: "/events", live: false },
  { label: "Engagements", to: "/events", live: false },
  { label: "Weddings", to: "/events", live: false },
  { label: "Corporate events", to: "/events", live: false },
];

const NAV_LINKS = [
  { label: "How it works", to: "/how-it-works" },
  { label: "FAQ", to: "/faq" },
  { label: "Partners", to: "/partners" },
  { label: "About", to: "/about" },
  { label: "Careers", to: "/careers" },
];

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [eventsOpen, setEventsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setEventsOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const handleLogoClick = () => {
    setIsOpen(false);
    navigate("/");
  };

  const isActive = (to) => location.pathname === to;

  return (
    <header className="sticky top-0 z-40 bg-paper border-b border-line">
      <div className="max-w-content mx-auto flex items-center justify-between px-6 py-4 lg:px-10">
        <button onClick={handleLogoClick} aria-label="Nirkana home">
          <img src="/assets/logo.png" alt="Nirkana" className="h-12 w-auto sm:h-14" />
        </button>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-7">
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setEventsOpen(!eventsOpen)}
              className="flex items-center gap-1 text-[15px] text-charcoal hover:text-action transition-colors"
              aria-expanded={eventsOpen}
            >
              Events
              <FiChevronDown size={15} className={`transition-transform ${eventsOpen ? "rotate-180" : ""}`} />
            </button>

            {eventsOpen && (
              <div className="absolute left-0 top-full mt-3 w-72 rounded-xl border border-line bg-paper py-2 shadow-lg">
                {EVENT_TYPES.map((item) => (
                  <Link
                    key={item.label}
                    to={item.to}
                    onClick={() => setEventsOpen(false)}
                    className="flex items-center justify-between px-4 py-2.5 text-sm text-charcoal hover:bg-cloud"
                  >
                    {item.label}
                    {!item.live && (
                      <span className="rounded-full bg-brand-soft px-2 py-0.5 text-[11px] font-medium text-brand">
                        Coming soon
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {NAV_LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`text-[15px] transition-colors ${
                isActive(link.to) ? "text-brand font-medium" : "text-charcoal hover:text-action"
              }`}
            >
              {link.label}
            </Link>
          ))}

          <Link
            to="/book"
            className="ml-1 rounded-full bg-action px-5 py-2.5 text-[15px] font-medium text-paper hover:bg-action/90 transition-colors"
          >
            Get started
          </Link>
        </nav>

        <button
          className="lg:hidden text-brand p-1"
          onClick={() => setIsOpen(!isOpen)}
          aria-label={isOpen ? "Close menu" : "Open menu"}
          aria-expanded={isOpen}
        >
          {isOpen ? <FiX size={24} /> : <FiMenu size={24} />}
        </button>
      </div>

      {/* Mobile nav */}
      {isOpen && (
        <div className="lg:hidden border-t border-line bg-paper px-6 py-6 space-y-1 max-h-[80vh] overflow-y-auto">
          <p className="pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-muted">Events</p>
          {EVENT_TYPES.map((item) => (
            <Link
              key={item.label}
              to={item.to}
              onClick={() => setIsOpen(false)}
              className="flex items-center justify-between py-3 text-base text-charcoal border-b border-line"
            >
              {item.label}
              {!item.live && <span className="text-xs text-muted">Coming soon</span>}
            </Link>
          ))}

          <p className="pt-4 pb-1 text-xs font-semibold uppercase tracking-wide text-muted">Explore</p>
          {NAV_LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              onClick={() => setIsOpen(false)}
              className={`block py-3 text-base border-b border-line last:border-b-0 ${
                isActive(link.to) ? "text-brand font-medium" : "text-charcoal"
              }`}
            >
              {link.label}
            </Link>
          ))}

          <Link
            to="/book"
            onClick={() => setIsOpen(false)}
            className="mt-4 block rounded-full bg-action px-5 py-3 text-center text-base font-medium text-paper"
          >
            Get started
          </Link>
        </div>
      )}
    </header>
  );
};

export default Header;
